import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Clock, Globe, Search, ShieldCheck, Terminal, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { adminAPI } from '../services/api';
import { formatLocalizedDateTime } from '../utils/i18nFormatters';
import { normalizeAuditResponse } from '../utils/auditLogView';

const PAGE_SIZE = 20;

function AuditEventSummary({ log, t }) {
  if (log.action === 'LOGIN_SUCCESS' || log.action === 'LOGIN_FAILED') {
    return (
      <div className={`inline-flex rounded-lg border px-2.5 py-1 text-xs font-black ${log.action === 'LOGIN_SUCCESS' ? 'border-emerald-300 bg-emerald-50 text-emerald-900' : 'border-rose-300 bg-rose-50 text-rose-900'}`}>
        {t(log.action === 'LOGIN_SUCCESS' ? 'finance.audit.events.loginSuccess' : 'finance.audit.events.loginFailed')}
      </div>
    );
  }
  if (log.action === 'ACCESS_COMPLETE') {
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="break-all rounded-lg border border-slate-300 bg-slate-50 px-2.5 py-1 font-mono font-bold text-slate-900">
          {log.details?.method || '-'} {log.details?.path || '-'}
        </span>
        <span className="rounded-lg border border-slate-300 px-2.5 py-1 font-black text-slate-800">HTTP {log.details?.statusCode ?? '-'}</span>
        <span className="admin-text-muted">{t('finance.audit.access.duration')} {log.details?.durationMs ?? '-'} ms</span>
      </div>
    );
  }
  return null;
}

function AuditDetails({ log, expanded, onToggle, t }) {
  const fields = log.summary?.changedFields || [];
  return (
    <div className="space-y-2">
      <AuditEventSummary log={log} t={t} />
      <div className="flex flex-wrap items-center gap-2">
        {fields.slice(0, 4).map((field) => (
          <span key={field} className="rounded-full border border-cyan-300 bg-cyan-50 px-2 py-1 text-[11px] font-semibold text-cyan-900">
            {field}
          </span>
        ))}
        {fields.length > 4 ? (
          <span className="text-xs admin-text-muted">+{fields.length - 4}</span>
        ) : null}
        {!fields.length ? <span className="text-xs admin-text-muted">{t('finance.audit.noChangedFields')}</span> : null}
      </div>
      {log.summary?.hasDetails ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="admin-secondary-button rounded-lg px-3 py-2 text-xs font-bold"
          data-testid={`audit-detail-toggle-${log.id ?? log._id}`}
        >
          {expanded ? t('finance.audit.hideDetails') : t('finance.audit.showDetails')}
        </button>
      ) : null}
      {expanded ? (
        <pre
          className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-slate-300 p-3 text-xs"
          style={{ backgroundColor: '#f8fafc', color: '#0f172a' }}
          data-testid={`audit-detail-content-${log.id ?? log._id}`}
        >
          {JSON.stringify(log.details, null, 2)}
        </pre>
      ) : null}
    </div>
  );
}

export default function AuditLogs() {
  const { t, i18n } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: PAGE_SIZE, pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [accountFilter, setAccountFilter] = useState('');
  const [debouncedAccount, setDebouncedAccount] = useState('');
  const [pathFilter, setPathFilter] = useState('');
  const [debouncedPath, setDebouncedPath] = useState('');
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedAccount(accountFilter.trim()), 300);
    return () => clearTimeout(timeout);
  }, [accountFilter]);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedPath(pathFilter.trim()), 300);
    return () => clearTimeout(timeout);
  }, [pathFilter]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    adminAPI.getAuditLogs({
      page,
      limit: PAGE_SIZE,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(moduleFilter.trim() ? { module: moduleFilter.trim() } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(eventFilter ? { event: eventFilter } : {}),
      ...(debouncedAccount ? { account: debouncedAccount } : {}),
      ...(debouncedPath ? { path: debouncedPath } : {}),
    }).then((response) => {
      if (!active) return;
      const normalized = normalizeAuditResponse(response.data || {});
      setLogs(normalized.logs);
      setPagination(normalized.pagination);
      setExpandedIds(new Set());
    }).catch((requestError) => {
      if (!active) return;
      setLogs([]);
      setError(requestError.response?.data?.message || requestError.message || t('finance.audit.loadError'));
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [debouncedAccount, debouncedPath, debouncedSearch, eventFilter, moduleFilter, page, statusFilter, t]);

  const rangeText = useMemo(() => {
    if (!pagination.total) return t('finance.audit.rangeEmpty');
    const start = (pagination.page - 1) * pagination.limit + 1;
    const end = Math.min(start + logs.length - 1, pagination.total);
    return t('finance.audit.range', { start, end, total: pagination.total });
  }, [logs.length, pagination, t]);

  const updateFilter = (setter, value) => {
    setPage(1);
    setter(value);
  };

  const toggleDetails = (id) => {
    const key = String(id);
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const renderLogCard = (log) => {
    const id = log.id ?? log._id;
    const key = String(id);
    return (
      <article key={key} className="admin-card space-y-3 rounded-lg p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-bold admin-text-primary">{log.module || '-'}</div>
            <div className="text-xs text-cyan-700">{log.action || '-'}</div>
          </div>
          <span className={`rounded-full border px-2 py-1 text-[11px] font-bold ${log.status === 'Failed' ? 'border-rose-300 bg-rose-50 text-rose-800' : 'border-emerald-300 bg-emerald-50 text-emerald-800'}`}>
            {log.status || 'Success'}
          </span>
        </div>
        <div className="grid gap-2 text-xs admin-text-secondary">
          <span>{formatLocalizedDateTime(log.createdAt, i18n.language)}</span>
          <span>{log.userName || t('finance.audit.system')} · {log.userRole || '-'}</span>
          <span className="font-mono">#{log.adminAccountId || '-'}</span>
          <span>{log.ipAddress || '-'}</span>
        </div>
        <AuditDetails log={log} expanded={expandedIds.has(key)} onToggle={() => toggleDetails(id)} t={t} />
      </article>
    );
  };

  return (
    <div className="space-y-6 p-4 admin-text-primary sm:p-6 lg:p-8">
      <header>
        <h2 className="flex items-center gap-3 text-2xl font-bold sm:text-3xl">
          <ShieldCheck className="text-cyan-600" size={32} />
          {t('finance.audit.title')}
        </h2>
        <p className="mt-1 text-sm admin-text-muted">{t('finance.audit.description')}</p>
      </header>

      <section className="admin-card grid gap-3 rounded-lg p-4 md:grid-cols-2 xl:grid-cols-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
          <input
            type="search"
            aria-label={t('finance.audit.filters.search')}
            value={search}
            onChange={(event) => updateFilter(setSearch, event.target.value)}
            placeholder={t('finance.audit.filters.search')}
            className="admin-input w-full rounded-lg py-3 pl-10 pr-3 text-sm outline-none"
          />
        </div>
        <input
          type="text"
          aria-label={t('finance.audit.filters.account')}
          value={accountFilter}
          onChange={(event) => updateFilter(setAccountFilter, event.target.value)}
          placeholder={t('finance.audit.filters.account')}
          className="admin-input rounded-lg px-3 py-3 text-sm outline-none"
        />
        <input
          type="search"
          aria-label={t('finance.audit.filters.path')}
          value={pathFilter}
          onChange={(event) => updateFilter(setPathFilter, event.target.value)}
          placeholder={t('finance.audit.filters.path')}
          className="admin-input rounded-lg px-3 py-3 text-sm outline-none"
        />
        <input
          type="text"
          aria-label={t('finance.audit.filters.module')}
          value={moduleFilter}
          onChange={(event) => updateFilter(setModuleFilter, event.target.value)}
          placeholder={t('finance.audit.filters.module')}
          className="admin-input rounded-lg px-3 py-3 text-sm outline-none"
        />
        <select
          aria-label={t('finance.audit.filters.event')}
          value={eventFilter}
          onChange={(event) => updateFilter(setEventFilter, event.target.value)}
          className="admin-input rounded-lg px-3 py-3 text-sm outline-none"
        >
          <option value="">{t('finance.audit.events.all')}</option>
          <option value="LOGIN_SUCCESS">{t('finance.audit.events.loginSuccess')}</option>
          <option value="LOGIN_FAILED">{t('finance.audit.events.loginFailed')}</option>
          <option value="ACCESS_COMPLETE">{t('finance.audit.events.accessComplete')}</option>
        </select>
        <select
          aria-label={t('finance.audit.filters.status')}
          value={statusFilter}
          onChange={(event) => updateFilter(setStatusFilter, event.target.value)}
          className="admin-input rounded-lg px-3 py-3 text-sm outline-none"
        >
          <option value="">{t('finance.audit.allStatuses')}</option>
          <option value="Success">Success</option>
          <option value="Failed">Failed</option>
        </select>
      </section>

      {error ? <div className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800">{error}</div> : null}

      <section className="admin-card rounded-lg p-4 sm:p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm font-semibold admin-text-secondary">{rangeText}</div>
          <div className="text-xs admin-text-muted">{t('finance.audit.detailsSafeHint')}</div>
        </div>

        <div className="hidden overflow-x-auto rounded-lg border border-slate-200 lg:block">
          <table className="w-full table-fixed text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase admin-text-secondary">
              <tr>
                <th className="w-[16%] p-4"><Clock size={14} className="mr-2 inline" />{t('finance.audit.table.timestamp')}</th>
                <th className="w-[16%] p-4"><User size={14} className="mr-2 inline" />{t('finance.audit.table.admin')}</th>
                <th className="w-[18%] p-4"><Terminal size={14} className="mr-2 inline" />{t('finance.audit.table.moduleAction')}</th>
                <th className="w-[38%] p-4">{t('finance.audit.table.details')}</th>
                <th className="w-[12%] p-4 text-right"><Globe size={14} className="mr-2 inline" />{t('finance.audit.table.ip')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="p-10 text-center admin-text-muted">{t('finance.audit.table.loading')}</td></tr>
              ) : !logs.length ? (
                <tr><td colSpan="5" className="p-10 text-center admin-text-muted">{t('finance.audit.table.noLogs')}</td></tr>
              ) : logs.map((log) => {
                const id = log.id ?? log._id;
                const key = String(id);
                return (
                  <tr key={key} className="border-t border-slate-200 align-top">
                    <td className="break-words p-4 text-xs admin-text-secondary">{formatLocalizedDateTime(log.createdAt, i18n.language)}</td>
                    <td className="p-4"><div className="font-semibold">{log.userName || t('finance.audit.system')}</div><div className="text-xs admin-text-muted">{log.userRole || '-'}</div><div className="mt-1 font-mono text-xs admin-text-muted">#{log.adminAccountId || '-'}</div></td>
                    <td className="p-4"><div className="font-semibold text-cyan-700">{log.module || '-'}</div><div className="break-words text-xs admin-text-muted">{log.action || '-'}</div></td>
                    <td className="p-4"><AuditDetails log={log} expanded={expandedIds.has(key)} onToggle={() => toggleDetails(id)} t={t} /></td>
                    <td className="break-all p-4 text-right text-xs admin-text-muted">{log.ipAddress || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="grid gap-3 lg:hidden">
          {loading ? <div className="p-8 text-center admin-text-muted">{t('finance.audit.table.loading')}</div> : logs.map(renderLogCard)}
          {!loading && !logs.length ? <div className="p-8 text-center admin-text-muted">{t('finance.audit.table.noLogs')}</div> : null}
        </div>

        <div className="mt-5 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setPage((value) => Math.max(value - 1, 1))}
            disabled={loading || pagination.page <= 1}
            className="admin-secondary-button inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-bold disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ChevronLeft size={16} />{t('finance.audit.previous')}
          </button>
          <span className="text-sm admin-text-secondary">{t('finance.audit.pageInfo', { page: pagination.page, pages: pagination.pages })}</span>
          <button
            type="button"
            onClick={() => setPage((value) => Math.min(value + 1, pagination.pages))}
            disabled={loading || pagination.page >= pagination.pages}
            className="admin-secondary-button inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-bold disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('finance.audit.next')}<ChevronRight size={16} />
          </button>
        </div>
      </section>
    </div>
  );
}
